/**
 * Is this connection going through TURN, or straight between the two machines?
 *
 * The documentation in [iceConfig](./iceConfig.ts) makes a claim about when a relay is needed. This
 * is what makes the claim checkable: it reads the nominated candidate pair out of `getStats` and
 * reports what kind of path actually got used, which is the difference between believing mobile
 * data works and having watched it work.
 *
 * `RTCStatsReport` is a map of loosely typed records that differ by browser, so every field is
 * checked at runtime here rather than asserted. Nothing downstream sees an unchecked value.
 *
 * Sampled slowly. Nothing here is on the frame path, and a candidate pair changes rarely.
 */

const SAMPLE_INTERVAL_MS = 5000;

export interface CandidateReport {
  /** True when either end of the pair is a TURN relay, so packets are being carried for us. */
  readonly relayed: boolean;
  /** `host`, `srflx`, `prflx` or `relay`, straight from the stats report. */
  readonly localType: string;
  readonly remoteType: string;
}

export interface CandidateWatch {
  stop(): void;
}

/** Polls until stopped, reporting only when the answer changes. */
export function watchCandidates(
  connection: RTCPeerConnection,
  onReport: (report: CandidateReport) => void,
): CandidateWatch {
  // A field rather than a local, so a sample that started before `stop` still sees the stop.
  const state = { stopped: false, last: '' };

  const sample = async (): Promise<void> => {
    const report = await readCandidatePair(connection);
    if (report === null || state.stopped) return;

    const key = `${report.localType}/${report.remoteType}`;
    if (key === state.last) return;
    state.last = key;
    onReport(report);
  };

  const timer = setInterval(() => {
    void sample();
  }, SAMPLE_INTERVAL_MS);
  void sample();

  return {
    stop(): void {
      state.stopped = true;
      clearInterval(timer);
    },
  };
}

/** Null while ICE is still choosing, which is most of the first second. */
export async function readCandidatePair(
  connection: RTCPeerConnection,
): Promise<CandidateReport | null> {
  const stats = await connection.getStats();
  const entries = new Map<string, Record<string, unknown>>();
  stats.forEach((value: unknown, key: string) => {
    if (isRecord(value)) entries.set(key, value);
  });

  const pair = [...entries.values()].find(
    (entry) => entry.type === 'candidate-pair' && entry.nominated === true,
  );
  if (pair === undefined) return null;

  const localType = candidateType(entries, pair.localCandidateId);
  const remoteType = candidateType(entries, pair.remoteCandidateId);
  return { relayed: localType === 'relay' || remoteType === 'relay', localType, remoteType };
}

function candidateType(entries: ReadonlyMap<string, Record<string, unknown>>, id: unknown): string {
  if (typeof id !== 'string') return 'unknown';
  const type = entries.get(id)?.candidateType;
  return typeof type === 'string' ? type : 'unknown';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}
